import { BarChart3, Copy, Megaphone, Pencil, Power, PowerOff, Send, TicketPercent } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Badge, Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui';
import { DataTablePagination, PanelInfo } from '@/components/custom';
import { broadcastService } from '@/services';
import { getApiErrorMessage, notify } from '@/functions';
import { useRequestPromoCodeActions } from '@/hooks/httpRequest/promoCodes';
import { PROMO_CODE_STATUS_CONFIG } from '@/constants';
import { PromoCodeBonusTypeEnum, PromoCodeStatusEnum } from '@/enums';
import { cn } from '@/lib/utils';
import type { IPromoCodeOffer } from '@/interfaces';
import DataTableBodySkeleton from './dataTable/DataTableBodySkeleton';
import DataTableMobileSkeleton from './dataTable/DataTableMobileSkeleton';

const formatDate = (date: string) => format(new Date(date), 'dd MMM yyyy HH:mm', { locale: fr });

const formatBonus = (offer: IPromoCodeOffer) =>
	offer.bonusType === PromoCodeBonusTypeEnum.PERCENT ? `+${offer.bonusValue}%` : `+${offer.bonusValue} pièces`;

const formatUses = (offer: IPromoCodeOffer) => `${offer.usedCount}${offer.maxUses ? ` / ${offer.maxUses}` : ''}`;

type TProps = {
	offers: IPromoCodeOffer[];
	isLoading: boolean;
	pageCount?: number;
	onEdit: (offer: IPromoCodeOffer) => void;
	onDuplicate: (offer: IPromoCodeOffer) => void;
	onViewStats: (offer: IPromoCodeOffer) => void;
};

const PromoCodesTable = ({ offers, isLoading, pageCount, onEdit, onDuplicate, onViewStats }: TProps) => {
	const { triggerSetDisabled, triggerAnnounce, isLoading: isActing } = useRequestPromoCodeActions();

	const handleSetDisabled = async (offer: IPromoCodeOffer, disabled: boolean) => {
		try {
			await triggerSetDisabled({ id: offer._id, disabled });
			notify(disabled ? 'Offre arrêtée.' : 'Offre relancée.');
		} catch (error) {
			notify(getApiErrorMessage(error, 'Erreur lors de la mise à jour.'), 'error');
		}
	};

	// the announcement reaches the members (or the one member of a personal code): confirm before it goes out
	const handleAnnounce = (offer: IPromoCodeOffer) => {
		broadcastService.modal.show({
			content: (
				<div className="flex flex-col items-center text-center space-y-4 py-2">
					<div className="space-y-1">
						<p className="font-medium text-foreground">Envoyer l'annonce maintenant ?</p>
						<p className="text-xs text-muted-foreground">
							{offer.targetEmail
								? `Message dans le chat Heyama et notification push à ${offer.targetEmail} uniquement. Une seule fois par offre.`
								: 'Message dans le chat Heyama et notification push à tous les membres. Une seule fois par offre.'}
						</p>
					</div>
					<div className="flex space-x-2 pt-2 w-full">
						<Button variant="outline" onClick={() => broadcastService.modal.hide()} size="sm" className="flex-1">
							Annuler
						</Button>
						<Button
							onClick={async () => {
								broadcastService.modal.hide();
								try {
									await triggerAnnounce(offer._id);
									notify('Annonce envoyée.');
								} catch (error) {
									notify(getApiErrorMessage(error, "Erreur lors de l'envoi."), 'error');
								}
							}}
							size="sm"
							className="flex-1"
						>
							Envoyer
						</Button>
					</div>
				</div>
			),
			width: 'xs',
			showCloseIcon: false,
		});
	};

	const renderActions = (offer: IPromoCodeOffer) => {
		const isDisabled = offer.status === PromoCodeStatusEnum.DISABLED;
		const canAnnounce = !!offer.announcement && !offer.notifiedAt && !isDisabled;
		return (
			<div className="flex items-center justify-end gap-1">
				<Button size="icon" variant="ghost" title="Statistiques" aria-label="Statistiques" onClick={() => onViewStats(offer)}>
					<BarChart3 className="h-4 w-4" />
				</Button>
				<Button size="icon" variant="ghost" title="Modifier" aria-label="Modifier" onClick={() => onEdit(offer)}>
					<Pencil className="h-4 w-4" />
				</Button>
				<Button size="icon" variant="ghost" title="Réutiliser avec un nouveau code" aria-label="Réutiliser avec un nouveau code" onClick={() => onDuplicate(offer)}>
					<Copy className="h-4 w-4" />
				</Button>
				{canAnnounce && (
					<Button size="icon" variant="ghost" title="Envoyer l'annonce maintenant" aria-label="Envoyer l'annonce maintenant" disabled={isActing} onClick={() => handleAnnounce(offer)}>
						<Send className="h-4 w-4" />
					</Button>
				)}
				{/* stopping works whatever the status; an ended offer cannot start again */}
				{isDisabled ? (
					<Button size="icon" variant="ghost" title="Relancer" aria-label="Relancer" disabled={isActing} onClick={() => handleSetDisabled(offer, false)}>
						<Power className="h-4 w-4" />
					</Button>
				) : (
					<Button size="icon" variant="ghost" title="Arrêter" aria-label="Arrêter" disabled={isActing} onClick={() => handleSetDisabled(offer, true)}>
						<PowerOff className="h-4 w-4 text-destructive" />
					</Button>
				)}
			</div>
		);
	};

	if (isLoading) {
		return (
			<div className="space-y-5">
				<div className="hidden md:block border rounded-md">
					<Table>
						<PromoCodesHeader />
						<DataTableBodySkeleton rows={8} />
					</Table>
				</div>
				<div className="block md:hidden">
					<DataTableMobileSkeleton count={5} />
				</div>
			</div>
		);
	}

	if (offers.length === 0) {
		return (
			<PanelInfo
				title="Aucun code promo"
				description="Aucun code promo ne correspond aux critères sélectionnés."
				className="py-10 md:py-26 border-none"
				icon={TicketPercent}
			/>
		);
	}

	return (
		<div className="space-y-5">
			<div className="hidden md:block border rounded-md">
				<Table>
					<PromoCodesHeader />
					<TableBody>
						{offers.map(offer => (
							<TableRow key={offer._id}>
								<TableCell className="font-medium">
									{offer.name}
									{offer.targetEmail && <p className="text-xs font-normal text-muted-foreground">Réservé à {offer.targetEmail}</p>}
								</TableCell>
								<TableCell className="font-mono">{offer.code}</TableCell>
								<TableCell>{formatBonus(offer)}</TableCell>
								<TableCell className="text-xs">
									{formatDate(offer.startsAt)}
									<br />
									{formatDate(offer.endsAt)}
								</TableCell>
								<TableCell>{formatUses(offer)}</TableCell>
								<TableCell>
									<Badge className={PROMO_CODE_STATUS_CONFIG[offer.status].className}>{PROMO_CODE_STATUS_CONFIG[offer.status].label}</Badge>
								</TableCell>
								<TableCell>{renderActions(offer)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			<div className="block md:hidden space-y-4">
				{offers.map(offer => (
					<div key={offer._id} className="p-4 border rounded-lg bg-card shadow-xs space-y-2">
						<div className="flex items-center justify-between gap-3">
							<span className="font-mono font-medium">{offer.code}</span>
							<Badge className={cn(PROMO_CODE_STATUS_CONFIG[offer.status].className)}>{PROMO_CODE_STATUS_CONFIG[offer.status].label}</Badge>
						</div>
						<p className="text-sm">{offer.name}</p>
						{offer.targetEmail && <p className="text-xs text-muted-foreground">Réservé à {offer.targetEmail}</p>}
						<p className="text-xs text-muted-foreground">
							{formatBonus(offer)} · {formatUses(offer)} utilisations
						</p>
						<p className="text-xs text-muted-foreground flex items-center gap-1">
							<Megaphone className="h-3 w-3" />
							{formatDate(offer.startsAt)} → {formatDate(offer.endsAt)}
						</p>
						{renderActions(offer)}
					</div>
				))}
			</div>

			{pageCount !== undefined && <DataTablePagination pageCount={pageCount} />}
		</div>
	);
};

const PromoCodesHeader = () => (
	<TableHeader>
		<TableRow>
			<TableHead>Offre</TableHead>
			<TableHead>Code</TableHead>
			<TableHead>Bonus</TableHead>
			<TableHead>Période</TableHead>
			<TableHead>Utilisations</TableHead>
			<TableHead>Statut</TableHead>
			<TableHead className="text-right">Actions</TableHead>
		</TableRow>
	</TableHeader>
);

export default PromoCodesTable;
